import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/providers/AuthProvider';
import { AuthDialog } from './AuthDialog';
import vewlyLogo from '@/assets/Logo_Horizonal_Transparent.png';

export function LandingHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 64; // Height of sticky header (h-16 = 4rem = 64px)
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Home', onClick: () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); } },
    { label: 'Features', onClick: () => scrollToSection('features') },
    { label: 'Pricing', onClick: () => scrollToSection('pricing') },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link to={user ? "/home" : "/"} className="flex items-center">
          <img src={vewlyLogo} alt="Vewly" width="105" height="40" className="h-10 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={item.onClick}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {item.label}
            </button>
          ))}
          <Link
            to="/public-downloads"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <Download className="h-4 w-4" />
            Downloads
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <Link to="/dashboard">
              <Button>Dashboard</Button>
            </Link>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setIsAuthDialogOpen(true)}>
                Sign In
              </Button>
              <Button onClick={() => setIsAuthDialogOpen(true)}>
                Get Started
              </Button>
            </>
          )}
        </div>
        
        <AuthDialog open={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen} />

        {/* Mobile Navigation */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px]">
            <nav className="flex flex-col gap-4 mt-8">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className="text-lg font-medium text-left hover:text-primary transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <Link
                to="/public-downloads"
                onClick={() => setMobileMenuOpen(false)}
                className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2"
              >
                <Download className="h-5 w-5" />
                Downloads
              </Link>
              <div className="flex flex-col gap-2 mt-4" onClick={() => setMobileMenuOpen(false)}>
                {user ? (
                  <Link to="/dashboard">
                    <Button className="w-full">Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Button variant="outline" className="w-full" onClick={() => setIsAuthDialogOpen(true)}>
                      Sign In
                    </Button>
                    <Button className="w-full" onClick={() => setIsAuthDialogOpen(true)}>
                      Get Started
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
